// ----------------------------------------------------------------------------
// One song from the library: key, tempo, CCLI and plays, lyrics, its recent
// setlists, edit and delete. The prototype's chord-chart grid and PDF chart
// upload have no backing data yet (see redesignFlags.songChordCharts / pdfCharts).
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { plansApi, songsApi } from '@app/api/organization';
import type { ServicePlan, Song } from '@app/types';
import { Toast } from '@app/components/Notifications';
import { Button, Card, ConfirmDialog, EmptyState, ErrorState, StatTile } from '@app/components/ui';
import { isPastDate, shortDate } from '@app/lib/format';
import { logger } from '@app/utils/logger';
import { redesignFlags } from '@app/featureFlags';
import { SongFormModal } from '../components/songs/SongFormModal';
import { EMPTY_SONG_FORM, lastPlayedLabel, type SongFormData } from '../components/songs/songUtils';

export const SongDetail: React.FC = () => {
  const { songId = '' } = useParams<{ songId: string }>();
  const { user } = useAuth();
  const { groups } = useGroup();
  const navigate = useNavigate();
  const churchId = user?.churchId;

  const [song, setSong] = useState<Song | null>(null);
  const [usedIn, setUsedIn] = useState<ServicePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<SongFormData>(EMPTY_SONG_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<'title' | 'artist', string>>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState<{ m: string; t: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    if (!churchId || !songId) return;
    setLoading(true);
    setFailed(false);
    try {
      const [s, plans] = await Promise.all([songsApi.getById(churchId, songId), plansApi.getRecent(churchId, 100).catch(() => [] as ServicePlan[])]);
      setSong(s);
      setUsedIn(
        plans
          .filter((p) => p.items.some((i) => i.songId === songId))
          .sort((a, b) => +new Date(b.date) - +new Date(a.date))
          .slice(0, 4),
      );
    } catch (err) {
      logger.error('Failed to load song', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [churchId, songId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = () => {
    if (!song) return;
    setForm({
      title: song.title,
      artist: song.author || '',
      key: song.defaultKey || 'C',
      tempo: song.defaultTempo?.toString() || '',
      ccliNumber: song.ccliNumber || '',
      youtubeUrl: song.youtubeUrl || '',
      lastPlayed: song.lastPlayed ? new Date(song.lastPlayed).toISOString().split('T')[0] : '',
    });
    setFormErrors({});
    setEditOpen(true);
  };

  const save = async () => {
    if (!song || !churchId) return;
    const er: typeof formErrors = {};
    if (!form.title.trim()) er.title = 'Add a title.';
    if (!form.artist.trim()) er.artist = 'Add the artist.';
    setFormErrors(er);
    if (Object.keys(er).length) return;
    setSaving(true);
    try {
      const updated = await songsApi.update(churchId, song.id, {
        title: form.title.trim(),
        author: form.artist.trim(),
        defaultKey: form.key,
        defaultTempo: parseInt(form.tempo) || undefined,
        ccliNumber: form.ccliNumber || undefined,
        youtubeUrl: form.youtubeUrl || undefined,
        lastPlayed: form.lastPlayed || undefined,
      });
      setSong(updated);
      setEditOpen(false);
      setToast({ m: 'Song updated', t: 'success' });
    } catch (err: any) {
      logger.error('Failed to update song', err);
      setToast({ m: err.message || 'Failed to update song', t: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!song || !churchId) return;
    try {
      await songsApi.delete(churchId, song.id);
      navigate('/admin/songs', { replace: true });
    } catch (err: any) {
      logger.error('Failed to delete song', err);
      setToast({ m: err.message || 'Failed to delete song', t: 'error' });
    }
  };

  const back = (
    <Link to="/admin/songs" className="inline-block text-[13.5px] mb-3.5">
      ← Song library
    </Link>
  );

  if (loading) {
    return (
      <div aria-busy="true">
        {back}
        <span className="wp-skeleton block h-10 w-72 rounded-[10px] mb-6" />
        <span className="wp-skeleton block h-64 rounded-[24px]" />
      </div>
    );
  }
  if (failed || !song) {
    return (
      <div>
        {back}
        <ErrorState title="Couldn't load this song" body="It may have been deleted, or the connection dropped." onRetry={load} />
      </div>
    );
  }

  const teamName = (id?: string) => groups.find((g) => g.id === id)?.name ?? 'No team';

  return (
    <div>
      {toast && <Toast message={toast.m} type={toast.t} onClose={() => setToast(null)} />}
      {back}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-[22px]">
        <div className="min-w-0">
          <div className="text-[13px] text-t4 mb-2">{song.author}</div>
          <h1 className="wp-h1">{song.title}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="danger-ghost" onClick={() => setConfirmDelete(true)} className="!bg-card">
            Delete song
          </Button>
          <Button variant="ghost" onClick={openEdit}>
            Edit
          </Button>
          {redesignFlags.pdfCharts && <Button variant="dark">Attach PDF chart</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-3.5 items-start">
        <Card className="flex-[2_1_520px] min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-[18px]">
            <h2 className="text-[16px] font-semibold">Lyrics</h2>
            {song.youtubeUrl && (
              <a href={song.youtubeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13.5px]">
                Listen on YouTube <ExternalLink size={13} aria-hidden="true" />
              </a>
            )}
          </div>
          {song.lyrics ? (
            <pre className="font-sans text-[14.5px] leading-[1.7] text-t2 whitespace-pre-wrap">{song.lyrics}</pre>
          ) : (
            <EmptyState
              title="No lyrics yet"
              body="Lyrics are added from a setlist item. Setlist emails skip songs without lyrics."
              className="!py-10"
            />
          )}
        </Card>

        <div className="flex-[1_1_300px] min-w-0 flex flex-col gap-3.5">
          <Card pad={20} className="grid grid-cols-2 gap-2.5">
            <StatTile label="Original key" value={song.defaultKey || '–'} />
            <StatTile label="Tempo" value={song.defaultTempo ? `${song.defaultTempo}` : '–'} />
            <StatTile label="CCLI" value={song.ccliNumber || 'Not set'} className="[&>div:last-child]:text-[14px] [&>div:last-child]:mt-[5px]" />
            <StatTile label="Plays" value={song.timesSung ?? 0} />
            <StatTile label="Last played" value={lastPlayedLabel(song.lastPlayed)} mono={false} className="col-span-2 [&>div:last-child]:text-[15px]" />
          </Card>
          <Card pad={20}>
            <h2 className="text-[15px] font-semibold mb-1.5">Recent setlists</h2>
            {usedIn.length === 0 ? (
              <p className="text-[13px] text-t4 py-2">Not in any setlist yet.</p>
            ) : (
              usedIn.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`/admin/setlists?plan=${p.id}&tab=${isPastDate(p.date) ? 'history' : 'upcoming'}`)}
                  className="flex justify-between gap-2.5 w-full text-left py-2.5 border-t border-line text-t1 text-[13.5px] group"
                >
                  <span>
                    <span className="font-medium group-hover:text-acc">{p.title}</span>
                    <span className="block text-[12px] text-t4 mt-px">{teamName(p.groupId)}</span>
                  </span>
                  <span className="text-t3 text-[12.5px] shrink-0">{shortDate(p.date)}</span>
                </button>
              ))
            )}
          </Card>
        </div>
      </div>

      <SongFormModal
        isOpen={editOpen}
        mode="edit"
        onClose={() => setEditOpen(false)}
        submitting={saving}
        formData={form}
        errors={formErrors}
        onChange={(patch) => setForm({ ...form, ...patch })}
        onSubmit={save}
      />
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        type="danger"
        title={`Delete "${song.title}"?`}
        message="It will be removed from the library and can no longer be added to setlists. Past setlists keep their record of it."
        confirmText="Delete song"
      />
    </div>
  );
};
